import { useCallback, useRef } from "react";
import { useLazyGetPrepaidTransactionByReferenceForCustomerQuery } from "../service/transaction-service";
import { Transaction } from "../model/transaction";
import { VendingStepData } from "../model/notifications/notifications";
import { VendingStepStatus, VendingStepType } from "../pages/customer/components/transaction-result-v2/types";
import { trackVendingOutcome } from "../services/business-analytics/vendEvents";
import { useVendingStatusListener } from "./useVendingStatusListener";

/**
 * Reports vending_success / vending_failed once per prepaid transaction.
 *
 * Vending runs after the payment is confirmed, so the transaction fetched right after
 * billing is often still in progress. The outcome is then picked up from the next final
 * vending step pushed over SignalR by re-fetching the transaction — the push itself carries
 * no token, units or error, only the step type and status.
 *
 * Returns `report(transaction)`: call it with every fetched transaction of the purchase.
 */
export const useVendingOutcomeEvents = () => {
	const [getTransaction] = useLazyGetPrepaidTransactionByReferenceForCustomerQuery();
	const reported = useRef(new Set<string>());
	const pending = useRef<string | null>(null);

	const report = useCallback((transaction: Transaction | null | undefined) => {
		const reference = transaction?.reference;
		if (!reference || reported.current.has(reference)) return;
		if (trackVendingOutcome(transaction)) {
			reported.current.add(reference);
			if (pending.current === reference) pending.current = null;
		} else {
			pending.current = reference;
		}
	}, []);

	const onVendingStep = useCallback(
		async (step: VendingStepData) => {
			const reference = pending.current;
			if (!reference) return;
			const type = Number(step.typeOfEvent);
			const status = Number(step.subTypeOfEvent);
			const isFinal = status === VendingStepStatus.SUCCESSFUL || status === VendingStepStatus.FAILED;
			if (type < VendingStepType.TOKEN_GENERATION || !isFinal) return;
			try {
				report(await getTransaction({ Reference: reference }).unwrap());
			} catch {
				// Leave it pending; the next step pushed for this purchase retries.
			}
		},
		[getTransaction, report]
	);

	useVendingStatusListener(onVendingStep);

	return report;
};
